
import { useState } from 'react';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import AppointmentModal from '../../../components/feature/AppointmentModal';

export default function ContactContent() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d3048eavrfupcr1qum7g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactMethods = [
    {
      icon: 'ri-phone-line',
      title: 'Telefon (Merkez)',
      value: '+90 543 437 30 49',
      link: 'tel:+905434373049',
      description: 'Hafta içi 09:00-18:00 arası'
    },
    {
      icon: 'ri-phone-line',
      title: 'Telefon (Alternatif)',
      value: '+90 532 623 01 83',
      link: 'tel:+905326230183',
      description: '7/24 acil destek'
    },
    {
      icon: 'ri-mail-line',
      title: 'E-posta',
      value: 'info@ecoventra.com',
      link: 'mailto:info@ecoventra.com',
      description: '24 saat içinde yanıt'
    },
    {
      icon: 'ri-whatsapp-line',
      title: 'WhatsApp',
      value: '+90 532 623 01 83',
      link: 'https://api.whatsapp.com/send?phone=905326230183&text=Merhaba, sürdürülebilirlik danışmanlığı hizmetleriniz hakkında bilgi almak istiyorum.',
      description: 'Anında destek'
    },
    {
      icon: 'ri-global-line',
      title: 'Web Sitesi',
      value: 'www.ecoventra.com',
      link: 'https://www.ecoventra.com',
      description: 'Online bilgi merkezi'
    },
    {
      icon: 'ri-calendar-line',
      title: 'Online Randevu',
      value: 'calendly.com/ecoventra',
      action: () => setIsAppointmentModalOpen(true),
      description: 'Ücretsiz danışmanlık'
    }
  ];

  const handleContactMethodClick = (method: any) => {
    if (method.action) {
      method.action();
    } else if (method.link) {
      window.open(method.link, method.link.startsWith('http') ? '_blank' : '_self');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="relative py-32 bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20business%20office%20contact%20center%20with%20customer%20service%20representatives%2C%20professional%20communication%20environment%2C%20clean%20workspace%20with%20computers%20and%20headsets%2C%20corporate%20customer%20support&width=1200&height=600&seq=200&orientation=landscape)' 
        }}
      >
        <div className="absolute inset-0 bg-emerald-900/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6 animate-in fade-in slide-in-from-bottom-8 duration-1000">
            Bizimle İletişime Geçin
          </h1>
          <p className="text-xl text-emerald-100 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
            Sürdürülebilirlik yolculuğunuzda size nasıl yardımcı olabileceğimizi öğrenmek için 
            uzman ekibimizle iletişime geçin
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {contactMethods.map((method, index) => (
              <Card 
                key={index} 
                className="p-6 text-center group hover:shadow-xl hover:scale-105 transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
                onClick={() => handleContactMethodClick(method)}
              >
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-200 transition-colors">
                  <i className={`${method.icon} text-2xl text-emerald-600`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{method.title}</h3>
                <div className="text-emerald-600 font-medium hover:text-emerald-700 transition-colors">
                  {method.value}
                </div>
                <p className="text-sm text-gray-500 mt-2">{method.description}</p>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="animate-in fade-in slide-in-from-left duration-1000">
              <Card className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Mesaj Gönderin
                </h2>
                
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-line text-3xl text-emerald-600"></i>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Mesajınız Gönderildi
                    </h3>
                    <p className="text-gray-600 mb-6">
                      En kısa sürede sizinle iletişime geçeceğiz.
                    </p>
                    <Button onClick={() => setSubmitted(false)}>
                      Yeni Mesaj Gönder
                    </Button>
                  </div>
                ) : (
                  <form id="iletisim-formu" onSubmit={handleSubmit} data-readdy-form>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ad Soyad *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm transition-all duration-200"
                          placeholder="Adınız ve soyadınız"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          E-posta *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm transition-all duration-200"
                          placeholder="e-posta@ornek.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Telefon
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm transition-all duration-200"
                          placeholder="+90 5XX XXX XX XX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Şirket Adı
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm transition-all duration-200"
                          placeholder="Şirket adınız"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Konu
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 transition-all duration-200"
                      >
                        <option value="">Konu seçiniz</option>
                        <option value="sustainability">Sürdürülebilirlik Danışmanlığı</option>
                        <option value="gri">GRI Raporlama</option>
                        <option value="tsrs">TSRS Raporlama</option>
                        <option value="carbon">Karbon Ayak İzi</option>
                        <option value="water">Su Ayak İzi</option>
                        <option value="esg">ESG Danışmanlığı</option>
                        <option value="software">Yazılım Çözümleri</option>
                        <option value="training">Eğitim Hizmetleri</option>
                        <option value="partnership">İş Ortaklığı</option>
                        <option value="other">Diğer</option>
                      </select>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mesajınız *
                      </label>
                      <textarea
                        name="message"
                        rows={5}
                        required
                        value={formData.message}
                        onChange={handleChange}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm transition-all duration-200"
                        placeholder="Mesajınızı buraya yazın..."
                      ></textarea>
                      <div className="text-xs text-gray-500 mt-1 flex justify-between">
                        <span>{formData.message.length}/500 karakter</span>
                        {formData.message.length > 450 && (
                          <span className="text-orange-500">Karakter limitine yaklaşıyorsunuz</span>
                        )}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting || formData.message.length > 500}
                      className="w-full hover:scale-105 transition-all duration-200"
                      size="lg"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 flex items-center justify-center mr-2">
                            <i className="ri-loader-4-line animate-spin"></i>
                          </div>
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <div className="w-5 h-5 flex items-center justify-center mr-2">
                            <i className="ri-send-plane-line"></i>
                          </div>
                          Mesaj Gönder
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </Card>
            </div>

            {/* Office Location */}
            <div className="animate-in fade-in slide-in-from-right duration-1000 delay-300">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Ofis Lokasyonumuz
              </h2>
              
              <Card className="p-6 hover:shadow-lg transition-all duration-300 mb-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-xl text-emerald-600"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">İstanbul (Merkez)</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-start space-x-2">
                        <i className="ri-map-pin-line text-emerald-600 mt-1 flex-shrink-0"></i>
                        <span>Büyükdere Cad. No: 185 Kanyon Ofis Binası Kat: 9<br />34394 Levent/İstanbul</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="ri-phone-line text-emerald-600 flex-shrink-0"></i>
                        <a href="tel:+905434373049" className="hover:text-emerald-600 transition-colors">
                          +90 543 437 30 49
                        </a>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="ri-mail-line text-emerald-600 flex-shrink-0"></i>
                        <a href="mailto:info@ecoventra.com" className="hover:text-emerald-600 transition-colors">
                          info@ecoventra.com
                        </a>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="ri-whatsapp-line text-emerald-600 flex-shrink-0"></i>
                        <a 
                          href="https://api.whatsapp.com/send?phone=905326230183&text=Merhaba, sürdürülebilirlik danışmanlığı hizmetleriniz hakkında bilgi almak istiyorum." 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-emerald-600 transition-colors"
                        >
                          WhatsApp ile İletişim
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="p-4 text-center hover:shadow-lg transition-all duration-300 cursor-pointer group">
                  <a 
                    href="https://api.whatsapp.com/send?phone=905326230183&text=Merhaba, sürdürülebilirlik danışmanlığı hizmetleriniz hakkında bilgi almak istiyorum."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-green-200 transition-colors">
                      <i className="ri-whatsapp-line text-2xl text-green-600"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">WhatsApp</h3>
                    <p className="text-xs text-gray-500">Anında mesaj gönderin</p>
                  </a>
                </Card>

                <Card 
                  className="p-4 text-center hover:shadow-lg transition-all duration-300 cursor-pointer group"
                  onClick={() => setIsAppointmentModalOpen(true)}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-blue-200 transition-colors">
                    <i className="ri-calendar-line text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Online Randevu</h3>
                  <p className="text-xs text-gray-500">Ücretsiz danışmanlık</p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Haritada Bulun
          </h2>
          <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.354!2d29.0236!3d41.0825!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab7d35b92b005%3A0x1b8c5e4a1c9f1b8!2sKanyon%20Shopping%20Mall!5e0!3m2!1sen!2str!4v1647875892345!5m2!1sen!2str"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="ECOVENTRA Ofis Lokasyonu"
            ></iframe>
          </Card>
        </div>
      </section>

      {/* Appointment Modal */}
      <AppointmentModal 
        isOpen={isAppointmentModalOpen} 
        onClose={() => setIsAppointmentModalOpen(false)} 
      />
    </div>
  );
}
